// CSV, Excel and PDF export utilities
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { DM_TYPES, ILLUSTRATION_LEVELS, findLocationPreset, FX_SUPPORTED_CODES } from "./s1000dData";
import { computeTotals, computeResourcing, fmtCurrency, fmtHours, fmtPercent, fmtFte } from "./calculations";

const LEVEL_KEYS = ["simple", "intermediate", "complex"];

// Standard one-liner appended to PDF/Excel/CSV exports when the currency in
// use has no live ECB rate. Mirrors the on-screen Compare-tab banner so a
// printed copy carries the same caveat.
const FX_FALLBACK_NOTE =
  "FX 1:1 fallback in play — this currency is not part of the ECB live-rates set. " +
  "Any cross-currency comparison will fall back to a 1:1 conversion.";

function isFxFallback(estimate) {
  const code = estimate?.currency?.code || "USD";
  return !FX_SUPPORTED_CODES.includes(code);
}

function safeFilename(name) {
  return (name || "estimate").replace(/[^a-z0-9-_]+/gi, "_");
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const getSym = (e) => e.currency?.symbol || "$";
const getCode = (e) => e.currency?.code || "USD";

export function exportCsv(estimate) {
  const totals = computeTotals(estimate);
  const resourcing = computeResourcing(totals, estimate.resourcePlan);
  const code = getCode(estimate);
  const plan = estimate.resourcePlan || {};
  const preset = findLocationPreset(plan.locationPreset || "AU");
  const rows = [];

  rows.push(["S1000D Cost Estimation Report"]);
  rows.push(["Project", estimate.projectName || "Untitled"]);
  rows.push(["Currency", code]);
  rows.push(["Generated", new Date().toISOString()]);
  if (isFxFallback(estimate)) {
    rows.push(["FX status", FX_FALLBACK_NOTE]);
  }
  rows.push([]);
  rows.push(["SUMMARY"]);
  rows.push(["Total Data Modules", totals.totalDms]);
  rows.push(["Total Illustrations", totals.totalIllus]);
  rows.push(["DM Authoring Hours", totals.dmHours.toFixed(2)]);
  rows.push(["Illustration Hours", totals.illusHours.toFixed(2)]);
  rows.push(["Total Project Hours", totals.totalHours.toFixed(2)]);
  rows.push([`Blended Hourly Rate (${code})`, totals.blendedRate.toFixed(2)]);
  rows.push([`Base Cost (${code})`, totals.baseCost.toFixed(2)]);
  rows.push([`Overhead (${estimate.overheadPercent || 0}%)`, totals.overheadCost.toFixed(2)]);
  rows.push([`Contingency (${estimate.contingencyPercent || 0}%)`, totals.contingencyCost.toFixed(2)]);
  rows.push([`GRAND TOTAL (${code})`, totals.grandTotal.toFixed(2)]);
  rows.push([]);
  rows.push(["RESOURCING"]);
  rows.push(["Location", preset.label]);
  rows.push(["Start Date", plan.startDate || ""]);
  rows.push(["End Date", plan.endDate || ""]);
  rows.push(["Public Holidays", Number(plan.publicHolidays) || 0]);
  rows.push(["Standard hrs / day", Number(plan.standardHoursPerDay) || 0]);
  rows.push(["Effective hrs / day", Number(plan.effectiveHoursPerDay) || 0]);
  rows.push(["Working Days", resourcing.workingDays]);
  rows.push(["Available Standard Hours", resourcing.availableStandardHours.toFixed(2)]);
  rows.push(["Available Effective Hours", resourcing.availableEffectiveHours.toFixed(2)]);
  rows.push(["Required FTE (Effective)", resourcing.fteEffective.toFixed(2)]);
  rows.push(["Required FTE (Standard)", resourcing.fteStandard.toFixed(2)]);
  rows.push(["Recommended Headcount", resourcing.totalHeadcount]);
  rows.push([]);

  rows.push(["DATA MODULES MATRIX"]);
  rows.push([
    "Code", "Type",
    "Simple Qty", "Simple Hrs/unit", "Simple Total Hrs",
    "Intermediate Qty", "Intermediate Hrs/unit", "Intermediate Total Hrs",
    "Complex Qty", "Complex Hrs/unit", "Complex Total Hrs",
    "Type Total Hrs",
  ]);
  for (const t of DM_TYPES) {
    const row = estimate.dmMatrix[t.code] || {};
    const s = row.simple || { qty: 0, hours: 0 };
    const i = row.intermediate || { qty: 0, hours: 0 };
    const c = row.complex || { qty: 0, hours: 0 };
    rows.push([
      t.code, t.name,
      s.qty, s.hours, (s.qty * s.hours).toFixed(2),
      i.qty, i.hours, (i.qty * i.hours).toFixed(2),
      c.qty, c.hours, (c.qty * c.hours).toFixed(2),
      (totals.perTypeHours[t.code] || 0).toFixed(2),
    ]);
  }

  rows.push([]);
  rows.push(["ILLUSTRATIONS"]);
  rows.push(["Level", "Qty", "Hrs/unit", "Total Hrs"]);
  for (const lvl of ILLUSTRATION_LEVELS) {
    const cell = estimate.illustrations[lvl.key] || { qty: 0, hours: 0 };
    rows.push([lvl.label, cell.qty, cell.hours, (cell.qty * cell.hours).toFixed(2)]);
  }

  rows.push([]);
  rows.push(["ROLES & COST BREAKDOWN"]);
  rows.push(["Role", `Hourly Rate (${code})`, "Effort %", "Hours Allocated", `Cost (${code})`, "% of Base Cost"]);
  for (const r of totals.roleBreakdown) {
    rows.push([
      r.name, r.hourlyRate.toFixed(2), r.effortPercent.toFixed(2),
      r.roleHours.toFixed(2), r.roleCost.toFixed(2), r.costPercent.toFixed(2),
    ]);
  }

  const csv = rows
    .map((r) =>
      r.map((c) => {
        const s = String(c ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `${safeFilename(estimate.projectName)}_S1000D_Estimate.csv`);
}

export function exportExcel(estimate) {
  const totals = computeTotals(estimate);
  const resourcing = computeResourcing(totals, estimate.resourcePlan);
  const code = getCode(estimate);
  const plan = estimate.resourcePlan || {};
  const preset = findLocationPreset(plan.locationPreset || "AU");

  const wb = XLSX.utils.book_new();

  // Summary sheet
  const summaryRows = [
    ["S1000D Cost Estimation Report"],
    ["Project", estimate.projectName || "Untitled"],
    ["Currency", code],
    ["Generated", new Date().toISOString()],
    ...(isFxFallback(estimate) ? [["FX status", FX_FALLBACK_NOTE]] : []),
    [],
    ["Metric", "Value"],
    ["Total Data Modules", totals.totalDms],
    ["Total Illustrations", totals.totalIllus],
    ["DM Authoring Hours", Number(totals.dmHours.toFixed(2))],
    ["Illustration Hours", Number(totals.illusHours.toFixed(2))],
    ["Total Project Hours", Number(totals.totalHours.toFixed(2))],
    [`Blended Hourly Rate (${code})`, Number(totals.blendedRate.toFixed(2))],
    [`Base Cost (${code})`, Number(totals.baseCost.toFixed(2))],
    [`Overhead ${estimate.overheadPercent || 0}% (${code})`, Number(totals.overheadCost.toFixed(2))],
    [`Contingency ${estimate.contingencyPercent || 0}% (${code})`, Number(totals.contingencyCost.toFixed(2))],
    [`GRAND TOTAL (${code})`, Number(totals.grandTotal.toFixed(2))],
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
  wsSummary["!cols"] = [{ wch: 36 }, { wch: 22 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  // DM Matrix sheet
  const dmHead = [
    "Code", "Type",
    "S Qty", "S Hrs", "S Total Hrs",
    "I Qty", "I Hrs", "I Total Hrs",
    "C Qty", "C Hrs", "C Total Hrs",
    "Type Total Hrs",
  ];
  const dmRows = [dmHead];
  for (const t of DM_TYPES) {
    const r = estimate.dmMatrix[t.code] || {};
    const s = r.simple || { qty: 0, hours: 0 };
    const i = r.intermediate || { qty: 0, hours: 0 };
    const c = r.complex || { qty: 0, hours: 0 };
    dmRows.push([
      t.code, t.name,
      Number(s.qty), Number(s.hours), Number((s.qty * s.hours).toFixed(2)),
      Number(i.qty), Number(i.hours), Number((i.qty * i.hours).toFixed(2)),
      Number(c.qty), Number(c.hours), Number((c.qty * c.hours).toFixed(2)),
      Number((totals.perTypeHours[t.code] || 0).toFixed(2)),
    ]);
  }
  const wsDm = XLSX.utils.aoa_to_sheet(dmRows);
  wsDm["!cols"] = [{ wch: 8 }, { wch: 30 }, ...Array(10).fill({ wch: 10 })];
  XLSX.utils.book_append_sheet(wb, wsDm, "Data Modules");

  // Illustrations
  const ilRows = [["Level", "Qty", "Hrs/unit", "Total Hrs"]];
  for (const lvl of ILLUSTRATION_LEVELS) {
    const c = estimate.illustrations[lvl.key] || { qty: 0, hours: 0 };
    ilRows.push([lvl.label, Number(c.qty), Number(c.hours), Number((c.qty * c.hours).toFixed(2))]);
  }
  const wsIl = XLSX.utils.aoa_to_sheet(ilRows);
  wsIl["!cols"] = [{ wch: 18 }, { wch: 10 }, { wch: 12 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, wsIl, "Illustrations");

  // Roles
  const roleRows = [["Role", `Hourly Rate (${code})`, "Effort %", "Hours", `Cost (${code})`, "% of Base Cost", "FTE Effective", "FTE Standard", "Headcount"]];
  const roleFte = resourcing.roleFteBreakdown;
  for (let i = 0; i < totals.roleBreakdown.length; i++) {
    const r = totals.roleBreakdown[i];
    const f = roleFte[i] || { fteEffective: 0, fteStandard: 0, headcount: 0 };
    roleRows.push([
      r.name,
      Number(r.hourlyRate.toFixed(2)),
      Number(r.effortPercent.toFixed(2)),
      Number(r.roleHours.toFixed(2)),
      Number(r.roleCost.toFixed(2)),
      Number(r.costPercent.toFixed(2)),
      Number(f.fteEffective.toFixed(2)),
      Number(f.fteStandard.toFixed(2)),
      f.headcount,
    ]);
  }
  const wsRoles = XLSX.utils.aoa_to_sheet(roleRows);
  wsRoles["!cols"] = [{ wch: 32 }, { wch: 16 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, wsRoles, "Roles");

  // Resourcing sheet
  const resRows = [
    ["Resource Plan"],
    ["Location", preset.label],
    ["Weekend Days", (plan.weekendDays || []).join(",")],
    ["Start Date", plan.startDate || ""],
    ["End Date", plan.endDate || ""],
    ["Public Holidays", Number(plan.publicHolidays) || 0],
    ["Standard hrs / day", Number(plan.standardHoursPerDay) || 0],
    ["Effective hrs / day", Number(plan.effectiveHoursPerDay) || 0],
    [],
    ["Calendar Days", resourcing.calendarDays],
    ["Weekends in window", resourcing.weekendsCount],
    ["Working Days", resourcing.workingDays],
    ["Available Standard Hours", Number(resourcing.availableStandardHours.toFixed(2))],
    ["Available Effective Hours", Number(resourcing.availableEffectiveHours.toFixed(2))],
    ["Total Project Hours", Number(resourcing.totalProjectHours.toFixed(2))],
    ["Required FTE (Effective)", Number(resourcing.fteEffective.toFixed(2))],
    ["Required FTE (Standard)", Number(resourcing.fteStandard.toFixed(2))],
    ["Recommended Headcount", resourcing.totalHeadcount],
  ];
  const wsRes = XLSX.utils.aoa_to_sheet(resRows);
  wsRes["!cols"] = [{ wch: 32 }, { wch: 22 }];
  XLSX.utils.book_append_sheet(wb, wsRes, "Resourcing");

  const out = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  triggerDownload(blob, `${safeFilename(estimate.projectName)}_S1000D_Estimate.xlsx`);
}

export function exportPdf(estimate) {
  const totals = computeTotals(estimate);
  const resourcing = computeResourcing(totals, estimate.resourcePlan);
  const sym = getSym(estimate);
  const code = getCode(estimate);
  const plan = estimate.resourcePlan || {};
  const preset = findLocationPreset(plan.locationPreset || "AU");
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("S1000D Cost Estimation Report", 40, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Project: ${estimate.projectName || "Untitled"}`, 40, 70);
  doc.text(`Currency: ${code} (${sym})`, 40, 84);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 98);

  // FX 1:1 fallback note — only when the chosen currency has no ECB rate.
  let tableStartY = 115;
  if (isFxFallback(estimate)) {
    const noteY = 112;
    doc.setFillColor(255, 251, 235); // amber-50
    doc.setDrawColor(202, 138, 4); // amber-600
    doc.setLineWidth(1.2);
    doc.rect(40, noteY, pageW - 80, 22, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(202, 138, 4);
    doc.text("FX 1:1 FALLBACK", 48, noteY + 9);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 53, 15);
    doc.text(FX_FALLBACK_NOTE, 48, noteY + 18);
    doc.setTextColor(0, 0, 0); // reset
    tableStartY = noteY + 30;
  }

  autoTable(doc, {
    startY: tableStartY,
    head: [["Metric", "Value"]],
    body: [
      ["Total Data Modules", String(totals.totalDms)],
      ["Total Illustrations", String(totals.totalIllus)],
      ["DM Authoring Hours", fmtHours(totals.dmHours)],
      ["Illustration Hours", fmtHours(totals.illusHours)],
      ["Total Project Hours", fmtHours(totals.totalHours)],
      ["Blended Hourly Rate", fmtCurrency(totals.blendedRate, sym)],
      ["Base Cost", fmtCurrency(totals.baseCost, sym)],
      [`Overhead (${estimate.overheadPercent || 0}%)`, fmtCurrency(totals.overheadCost, sym)],
      [`Contingency (${estimate.contingencyPercent || 0}%)`, fmtCurrency(totals.contingencyCost, sym)],
      ["GRAND TOTAL", fmtCurrency(totals.grandTotal, sym)],
    ],
    styles: { font: "helvetica", fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [0, 0, 0], textColor: 255 },
    margin: { left: 40, right: 40 },
    tableWidth: pageW / 2 - 50,
  });

  // Resourcing block on the same page (right column)
  autoTable(doc, {
    startY: tableStartY,
    margin: { left: pageW / 2 + 10, right: 40 },
    tableWidth: pageW / 2 - 50,
    head: [["Resourcing", "Value"]],
    body: [
      ["Location", preset.label],
      ["Start / End", `${plan.startDate || "—"}  →  ${plan.endDate || "—"}`],
      ["Public Holidays", String(Number(plan.publicHolidays) || 0)],
      ["Standard / Effective hrs", `${plan.standardHoursPerDay || 0} / ${plan.effectiveHoursPerDay || 0}`],
      ["Working Days", String(resourcing.workingDays)],
      ["Available · Standard", fmtHours(resourcing.availableStandardHours)],
      ["Available · Effective", fmtHours(resourcing.availableEffectiveHours)],
      ["Required FTE (Effective)", fmtFte(resourcing.fteEffective)],
      ["Required FTE (Standard)", fmtFte(resourcing.fteStandard)],
      ["Recommended Headcount", String(resourcing.totalHeadcount)],
    ],
    styles: { font: "helvetica", fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [0, 47, 167], textColor: 255 },
  });

  // DM Matrix page
  const dmBody = DM_TYPES.map((t) => {
    const row = estimate.dmMatrix[t.code] || {};
    const s = row.simple || { qty: 0, hours: 0 };
    const i = row.intermediate || { qty: 0, hours: 0 };
    const c = row.complex || { qty: 0, hours: 0 };
    return [
      t.code, t.name,
      s.qty, s.hours, (s.qty * s.hours).toFixed(1),
      i.qty, i.hours, (i.qty * i.hours).toFixed(1),
      c.qty, c.hours, (c.qty * c.hours).toFixed(1),
      (totals.perTypeHours[t.code] || 0).toFixed(1),
    ];
  });
  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Data Modules Matrix", 40, 50);
  autoTable(doc, {
    startY: 60,
    head: [[
      "Code", "Type",
      "S Qty", "S Hrs", "S Total",
      "I Qty", "I Hrs", "I Total",
      "C Qty", "C Hrs", "C Total",
      "Type Total",
    ]],
    body: dmBody,
    styles: { font: "helvetica", fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [0, 0, 0], textColor: 255 },
    margin: { left: 40, right: 40 },
  });

  doc.addPage();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("Illustrations", 40, 50);
  autoTable(doc, {
    startY: 60,
    head: [["Level", "Qty", "Hrs/unit", "Total Hrs"]],
    body: ILLUSTRATION_LEVELS.map((lvl) => {
      const cell = estimate.illustrations[lvl.key] || { qty: 0, hours: 0 };
      return [lvl.label, cell.qty, cell.hours, (cell.qty * cell.hours).toFixed(1)];
    }),
    styles: { font: "helvetica", fontSize: 10, cellPadding: 4 },
    headStyles: { fillColor: [0, 0, 0], textColor: 255 },
    margin: { left: 40, right: 40 },
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  const after = doc.lastAutoTable.finalY + 30;
  doc.text("Roles, Cost & Resource Breakdown", 40, after);
  autoTable(doc, {
    startY: after + 10,
    head: [["Role", "Hourly Rate", "Effort %", "Hours", "Cost", "% of Base", "FTE Eff.", "FTE Std.", "HC"]],
    body: totals.roleBreakdown.map((r, i) => {
      const f = resourcing.roleFteBreakdown[i] || { fteEffective: 0, fteStandard: 0, headcount: 0 };
      return [
        r.name,
        fmtCurrency(r.hourlyRate, sym),
        fmtPercent(r.effortPercent),
        fmtHours(r.roleHours),
        fmtCurrency(r.roleCost, sym),
        fmtPercent(r.costPercent),
        fmtFte(f.fteEffective),
        fmtFte(f.fteStandard),
        String(f.headcount),
      ];
    }),
    styles: { font: "helvetica", fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [0, 47, 167], textColor: 255 },
    margin: { left: 40, right: 40 },
  });

  doc.save(`${safeFilename(estimate.projectName)}_S1000D_Estimate.pdf`);
}

// Ensure LEVEL_KEYS unused warning is silenced
void LEVEL_KEYS;
